import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CreatePartnerContractRequest,
  SavePartnerContractTemplateRequest,
  UpdatePartnerContractRequest,
} from '@interfaces/partners/contracts/requests/partner-contract.request';
import {
  PartnerContractTemplateResponse,
  PartnerContractTemplateVersionResponse,
} from '@interfaces/partners/contracts/responses/partner-contract-template.response';
import {
  PartnerContractListItemResponse,
  PartnerContractPartyType,
  PartnerContractResponse,
  PartnerContractVariableResponse,
} from '@interfaces/partners/contracts/responses/partner-contract.response';
import {
  PartnerContractRevisionListItemResponse,
  PartnerContractRevisionResponse,
} from '@interfaces/partners/contracts/responses/partner-contract-revision.response';
import { ContractFieldChangeResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import { VaultApiService } from '@services/api/vault-api.service';
import {
  ContractPdfFile,
  unwrapContractResult as unwrapResult,
} from '@services/http/contracts/unwrap-contract-result.operator';
import { fileNameFromContentDisposition } from '@src/app/core/utils/zip-download.utils';
import { Observable, filter, map } from 'rxjs';

/** Arquivo do PDF baixado, com o nome que o backend mandou no `Content-Disposition`. */
export type PartnerContractPdfFile = ContractPdfFile;

/**
 * Contrato de Parceria Comercial editável (card 3462): o modelo base versionado e as minutas por
 * parceiro. Rota base `api/v1/partner-contracts`.
 *
 * As escritas podem responder `ResultObject<T>` ou `ResultObject<ValidationResult<T>>` (padrão dos
 * endpoints de parceiro). O {@link unwrapResult} aceita os dois formatos: se o corpo tem cara de
 * `ValidationResult`, a recusa vira `ErrorResponse` (como faz o `unwrapValidation`); se não, o
 * objeto passa direto. Assim a tela não mostra "salvo" de uma recusa aninhada em `isValid: false`.
 */
@Injectable({
  providedIn: 'root',
})
export class PartnerContractsHttpService {
  private readonly BASE_URL = 'partner-contracts';

  constructor(private readonly apiService: VaultApiService) {}

  // --- Modelo base ---

  /**
   * Modelo vigente do tipo de parte. Pessoa jurídica: na primeira chamada do tenant o backend cria a
   * v1 a partir do modelo padrão. Pessoa física não tem modelo padrão: sem versão salva, `null`
   * (200, não é erro) e a tela oferece criar a partir do modelo de pessoa jurídica.
   */
  getCurrentTemplate(
    partyType: PartnerContractPartyType
  ): Observable<PartnerContractTemplateResponse | null> {
    return this.apiService
      .get<PartnerContractTemplateResponse>(`${this.BASE_URL}/template`, undefined, { partyType })
      .pipe(unwrapResult<PartnerContractTemplateResponse>());
  }

  /** Histórico de versões do modelo do tipo de parte, sem o corpo. */
  getTemplateVersions(
    partyType: PartnerContractPartyType
  ): Observable<PartnerContractTemplateVersionResponse[]> {
    return this.apiService
      .get<PartnerContractTemplateVersionResponse[]>(
        `${this.BASE_URL}/template/versions`,
        undefined,
        {
          partyType,
        }
      )
      .pipe(
        unwrapResult<PartnerContractTemplateVersionResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Uma versão do modelo, com o corpo, para leitura no histórico. */
  getTemplateVersion(id: string): Observable<PartnerContractTemplateResponse | null> {
    return this.apiService
      .get<PartnerContractTemplateResponse>(`${this.BASE_URL}/template/versions/${id}`)
      .pipe(unwrapResult<PartnerContractTemplateResponse>());
  }

  /** Salva uma versão nova do modelo do `payload.partyType`. As minutas já geradas não mudam. */
  saveTemplateVersion(
    payload: SavePartnerContractTemplateRequest
  ): Observable<PartnerContractTemplateResponse | null> {
    return this.apiService
      .post<PartnerContractTemplateResponse>(`${this.BASE_URL}/template`, payload)
      .pipe(unwrapResult<PartnerContractTemplateResponse>());
  }

  /** Catálogo de variáveis aceitas no texto (`{{chave}}` e o rótulo de cada uma). */
  getVariables(): Observable<PartnerContractVariableResponse[]> {
    return this.apiService
      .get<PartnerContractVariableResponse[]>(`${this.BASE_URL}/variables`)
      .pipe(
        unwrapResult<PartnerContractVariableResponse[]>(),
        map(list => list ?? [])
      );
  }

  // --- Minutas por parceiro ---

  /** Contratos de um parceiro. */
  getContractsByPartner(partnerId: string): Observable<PartnerContractListItemResponse[]> {
    return this.apiService
      .get<PartnerContractListItemResponse[]>(this.BASE_URL, undefined, { partnerId })
      .pipe(
        unwrapResult<PartnerContractListItemResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Uma minuta, para o editor. */
  getContract(id: string): Observable<PartnerContractResponse | null> {
    return this.apiService
      .get<PartnerContractResponse>(`${this.BASE_URL}/${id}`)
      .pipe(unwrapResult<PartnerContractResponse>());
  }

  /** Gera a minuta a partir do modelo vigente, com as variáveis já substituídas. */
  createContract(
    payload: CreatePartnerContractRequest
  ): Observable<PartnerContractResponse | null> {
    return this.apiService
      .post<PartnerContractResponse>(this.BASE_URL, payload)
      .pipe(unwrapResult<PartnerContractResponse>());
  }

  /**
   * Salva título, dados da parte e (só editor do texto) o corpo-fonte. Só em rascunho. O backend
   * devolve o texto já renderizado com os valores gravados.
   */
  updateContract(
    id: string,
    payload: UpdatePartnerContractRequest
  ): Observable<PartnerContractResponse | null> {
    return this.apiService
      .put<PartnerContractResponse>(`${this.BASE_URL}/${id}`, payload)
      .pipe(unwrapResult<PartnerContractResponse>());
  }

  /**
   * Substitui as variáveis que ainda restam no texto com os dados da parte **gravados**. Por isso a
   * tela salva antes de chamar isto.
   */
  refillVariables(id: string): Observable<PartnerContractResponse | null> {
    return this.apiService
      .post<PartnerContractResponse>(`${this.BASE_URL}/${id}/refill`, {})
      .pipe(unwrapResult<PartnerContractResponse>());
  }

  /** Finaliza a minuta: o texto trava até alguém reabrir. */
  finalizeContract(id: string): Observable<PartnerContractResponse | null> {
    return this.apiService
      .post<PartnerContractResponse>(`${this.BASE_URL}/${id}/finalize`, {})
      .pipe(unwrapResult<PartnerContractResponse>());
  }

  /** Volta a minuta finalizada para rascunho. */
  reopenContract(id: string): Observable<PartnerContractResponse | null> {
    return this.apiService
      .post<PartnerContractResponse>(`${this.BASE_URL}/${id}/reopen`, {})
      .pipe(unwrapResult<PartnerContractResponse>());
  }

  /**
   * Histórico de revisões da minuta (MG4): uma por criação, edição de texto/título, preenchimento de
   * variáveis, finalização e reabertura. Sem o corpo; o mais recente primeiro é a tela que ordena.
   */
  getContractRevisions(id: string): Observable<PartnerContractRevisionListItemResponse[]> {
    return this.apiService
      .get<PartnerContractRevisionListItemResponse[]>(`${this.BASE_URL}/${id}/revisions`)
      .pipe(
        unwrapResult<PartnerContractRevisionListItemResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Uma revisão com o texto, para pré-visualizar e reaproveitar. */
  getContractRevision(
    id: string,
    revisionId: string
  ): Observable<PartnerContractRevisionResponse | null> {
    return this.apiService
      .get<PartnerContractRevisionResponse>(`${this.BASE_URL}/${id}/revisions/${revisionId}`)
      .pipe(unwrapResult<PartnerContractRevisionResponse>());
  }

  /**
   * Alterações de valores da minuta (card 3643): uma linha por campo de valor (percentual) que mudou
   * numa gravação, com quem mudou, quando, de/para e o padrão. Permissão `VIEW`.
   */
  getContractFieldChanges(id: string): Observable<ContractFieldChangeResponse[]> {
    return this.apiService
      .get<ContractFieldChangeResponse[]>(`${this.BASE_URL}/${id}/field-changes`)
      .pipe(
        unwrapResult<ContractFieldChangeResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Exclui a minuta (exclusão lógica). Só rascunho. */
  deleteContract(id: string): Observable<unknown> {
    return this.apiService.delete<unknown>(`${this.BASE_URL}/${id}`).pipe(unwrapResult<unknown>());
  }

  /**
   * Baixa o PDF da minuta (capa Legatus + corpo).
   *
   * Vai pelo `downloadWithProgress` (e não pelo `get` com `responseType: 'blob'`) porque ele lê o
   * envelope de erro que chega como `Blob` e devolve a mensagem do backend como `ErrorResponse`.
   * O nome do arquivo vem do `Content-Disposition` (`filename*` com acento); sem o cabeçalho, cai
   * no `fallbackFileName`.
   */
  downloadContractPdf(id: string, fallbackFileName: string): Observable<PartnerContractPdfFile> {
    return this.apiService.downloadWithProgress('get', `${this.BASE_URL}/${id}/pdf`).pipe(
      filter(event => event.type === HttpEventType.Response),
      map(event => {
        const response = event as HttpResponse<Blob>;
        const fileName =
          fileNameFromContentDisposition(response.headers.get('Content-Disposition')) ??
          fallbackFileName;
        return { blob: response.body ?? new Blob([], { type: 'application/pdf' }), fileName };
      })
    );
  }
}
